import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import styledComp from "styled-components";
import Button from "./Button";
import Icon from "./Icon";
import { Icon as MuiIcon } from "@mui/material";
import { StyledText } from "./Text";
import {
  IconButton,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from "@mui/material";
import { useScreenClass } from "react-grid-system";
import { useMemo } from "react";
import MinimalSelect from "./MinimalSelect";
import InfoIcon from "@mui/icons-material/Info";
import { SemesterContext } from "../context/SemesterContext";
import axios from "axios";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[5],
  },
}));
type ItemType = {
  active: boolean;
};
interface Column {
  id: "date" | "required" | "optional";
  label: string;
  minWidth?: number;
  align?: "right";
  format?: (value: number) => string;
}

const columns: Column[] = [
  { id: "date", label: "Date", minWidth: 1 },
  { id: "required", label: "Required", minWidth: 1 },
  {
    id: "optional",
    label: "Optional",
    minWidth: 1,
  },
];
const StyledAddButton = styledComp(Button)`
  margin-right: 24px;
  background: linear-gradient(270deg, #3a84ee 0%, #1a5edf 100%);
  backdrop-filter: blur(44px);
  padding: 6px 12px;
  border-radius: 6px;
  &:hover:not(:disabled) {
    background-color: linear-gradient(270deg, #3a84ee 0%, #1a5edf 100%);
  }
`;

const CustomPaper = styled(Paper)`
  margin-bottom: 0px;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;
const CustomTableContainer = styled(TableContainer)`
  margin-bottom: 0px;
  width: auto;
  overflow-y: overlay;
  overflow-x: overlay;
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
`;

const Item = styled(Paper)<ItemType>(({ theme, active }) => ({
  ...theme.typography.body2,
  padding: "9px 12px",
  background: active ? "#C54B4D" : "#23273E",
  border: "1px solid #3C406C",
  textAlign: "center",
  fontFamily: "Montserrat",
  fontStyle: "normal",
  fontWeight: "400",
  fontSize: "12px",
  lineHeight: "18px",
  letterSpacing: "0.2px",
  color: "#E6E6E6",
  borderRadius: "6px",
  // ':first-of-type': {
  //   borderRadius: '6px 0px 0px 6px',
  // },
  // ':last-child': {
  //   borderRadius: '0px 6px 6px 0px',
  // },
}));
export type TableProps = {
  dateRange: Array<any>;
  children?: React.ReactNode;
  timeline: boolean;
  data?: any;
  selectedData?: any;
  backgroundColor?: string;
  cellBackground?: string;
  requiredWidth?: string;
  optionalWidth?: string;
  onConfirm?: () => void;
  reMapData?: (roadmapID: any) => void;
  isPersonalizedGenerated?: boolean;
  setIsPersonalizedGenerated?: (value: boolean) => void;
};

export default function StyledTable({
  children,
  dateRange,
  timeline,
  requiredWidth,
  backgroundColor,
  cellBackground,
  data,
  optionalWidth,
  onConfirm,
  reMapData,
  selectedData,
  isPersonalizedGenerated,
  setIsPersonalizedGenerated
}: TableProps): JSX.Element {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const screenClass = useScreenClass();
  const { innerWidth: width } = window;
  const isLarge = useMemo(() => ["xxl"].includes(screenClass), [screenClass]);
  const semesterContext = React.useContext(SemesterContext);

  const calculateOptWidth = (req: any, opt: any) => {
    const full =
      280 +
      92 +
      32 +
      parseInt(req.replace(/px/, "")) +
      parseInt(opt.replace(/px/, ""));
    if (width - full !== 0 && width >= full) {
      return parseInt(opt.replace(/px/, "")) + (width - full) + "px";
    }
    return opt;
  };

  return (
    <CustomPaper
      sx={{
        // width: 'auto',
        background: "transparent",
        color: "white",
      }}
    >
      <CustomTableContainer className="visible-scrollbar">
        <Table
          sx={{
            // On large screens fill the container; on smaller screens size to
            // the combined required + optional content so the inner scroll
            // shows the full roadmap.
            height: isLarge
              ? "100%"
              : `calc(${requiredWidth} + ${optionalWidth})`,
            width: "auto",
            overflow: "scroll hidden",
          }}
          stickyHeader
          aria-label="sticky table"
        >
          <TableHead>
            {timeline ? (
              <>
                <TableRow>
                  <>
                    <TableCell
                      sx={{
                        background: backgroundColor || "rgba(0, 0, 0)",
                        color: "white",
                        borderBottom: "unset",
                        borderRight: cellBackground
                          ? `1px solid ${cellBackground}`
                          : "1px solid #3C406C",
                        minWidth: "60px",
                        width: "60px",
                        maxWidth: "60px",
                        position: "sticky",
                        left: 0,
                        zIndex: 900,
                      }}
                      key="date"
                      align="center"
                    ></TableCell>
                    {dateRange.map((row: any, index) => (
                      <>
                        {
                          // row.add_drop_date ? (
                          //   <TableCell
                          //     sx={{
                          //       background: backgroundColor || "rgba(0, 0, 0)",
                          //       color: "#00ff10",
                          //       borderBottom: "unset",
                          //       borderRight: cellBackground
                          //         ? `1px solid ${cellBackground}`
                          //         : "1px solid #3C406C",
                          //       minWidth: "120px",
                          //       width: "120px",
                          //       maxWidth: "120px",
                          //       position: "sticky",
                          //       fontWeight: "bold",
                          //       left: 0,
                          //       zIndex: 800,
                          //     }}
                          //     key={"special for add"}
                          //     align="center"
                          //   >
                          //     <LightTooltip
                          //       title={
                          //         <React.Fragment>
                          //           <StyledText>Add drop date : {row.exact_date}</StyledText>
                          //         </React.Fragment>
                          //       }
                          //       arrow
                          //     >
                          //       <IconButton
                          //         style={{ padding: 0, verticalAlign: "top" }}
                          //       >
                          //         <InfoIcon style={{ color: "#00ff10" }} />
                          //       </IconButton>
                          //     </LightTooltip>
                          //     {row.date}
                          //   </TableCell>
                          // ) :

                          row.final_recording_date && row.final_assess_date ? (
                            <TableCell
                              sx={{
                                background: backgroundColor || "rgba(0, 0, 0)",
                                color: "#00ff10",
                                borderBottom: "unset",
                                borderRight: cellBackground
                                  ? `1px solid ${cellBackground}`
                                  : "1px solid #3C406C",
                                minWidth: "120px",
                                width: "120px",
                                maxWidth: "120px",
                                position: "sticky",
                                fontWeight: "bold",
                                left: 0,
                                zIndex: 800,
                              }}
                              key={"special for add"}
                              align="center"
                            >
                              <LightTooltip
                                title={
                                  <React.Fragment>
                                    <StyledText>
                                      Final Assessment Date : {row.assess_date}
                                    </StyledText>
                                    <br />
                                    <StyledText>
                                      Final Recording Date : {row.exact_date}
                                    </StyledText>
                                  </React.Fragment>
                                }
                                arrow
                              >
                                <IconButton
                                  style={{ padding: 0, verticalAlign: "top" }}
                                >
                                  <InfoIcon style={{ color: "#00ff10" }} />
                                </IconButton>
                              </LightTooltip>
                              {row.date}
                            </TableCell>
                          ) :
                           row.final_recording_date ? (
                            <TableCell
                              sx={{
                                background: backgroundColor || "rgba(0, 0, 0)",
                                color: "#00ff10",
                                borderBottom: "unset",
                                borderRight: cellBackground
                                  ? `1px solid ${cellBackground}`
                                  : "1px solid #3C406C",
                                minWidth: "120px",
                                width: "120px",
                                maxWidth: "120px",
                                position: "sticky",
                                fontWeight: "bold",
                                left: 0,
                                zIndex: 800,
                              }}
                              key={"special for add"}
                              align="center"
                            >
                              <LightTooltip
                                title={
                                  <React.Fragment>
                                    <StyledText>
                                      Final Recording date : {row.exact_date}
                                    </StyledText>
                                  </React.Fragment>
                                }
                                arrow
                              >
                                <IconButton
                                  style={{ padding: 0, verticalAlign: "top" }}
                                >
                                  <InfoIcon style={{ color: "#00ff10" }} />
                                </IconButton>
                              </LightTooltip>
                              {row.date}
                            </TableCell>
                          ) : row.final_assess_date ? (
                            <TableCell
                              sx={{
                                background: backgroundColor || "rgba(0, 0, 0)",
                                color: "yellow",
                                borderBottom: "unset",
                                borderRight: cellBackground
                                  ? `1px solid ${cellBackground}`
                                  : "1px solid #3C406C",
                                minWidth: "120px",
                                width: "120px",
                                maxWidth: "120px",
                                position: "sticky",
                                fontWeight: "bold",
                                left: 0,
                                zIndex: 800,
                              }}
                              key={"special for final"}
                              align="center"
                            >
                              <LightTooltip
                                title={
                                  <React.Fragment>
                                    <StyledText>
                                      Final Assessment date : {row.exact_date}
                                    </StyledText>
                                  </React.Fragment>
                                }
                                arrow
                              >
                                <IconButton
                                  style={{ padding: 0, verticalAlign: "top" }}
                                >
                                  <InfoIcon style={{ color: "yellow" }} />
                                </IconButton>
                              </LightTooltip>

                              {row.date}
                            </TableCell>
                          ) :  
                                                    
                          (
                            <TableCell
                              sx={{
                                background: backgroundColor || "rgba(0, 0, 0)",
                                color: "white",
                                borderBottom: "unset",
                                borderRight: cellBackground
                                  ? `1px solid ${cellBackground}`
                                  : "1px solid #3C406C",
                                minWidth: "120px",
                                width: "120px",
                                maxWidth: "120px",
                                position: "sticky",
                                left: 0,
                                zIndex: 800,
                              }}
                              key={index}
                              align="center"
                            >
                              {row.date}
                            </TableCell>
                          )
                        }
                      </>
                    ))}
                  </>
                </TableRow>
              </>
            ) : (
              <>
                <TableRow>
                  <>
                    <TableCell
                      sx={{
                        background: backgroundColor || "rgba(0, 0, 0)",
                        color: "white",
                        borderBottom: "1px solid #3C406C",
                        position: "sticky",
                        left: "0",
                        top: "0",
                        zIndex: "800",
                        minWidth: "60px",
                        width: "60px",
                        maxWidth: "60px",
                        whiteSpace: "nowrap",
                      }}
                      align="left"
                    >
                      {semesterContext.selectedSemester.is_open &&
                        selectedData && (
                          <StyledAddButton
                            onClick={() => {
                              onConfirm?.();
                            }}
                          >
                            Adopt Learning path
                            <Icon
                              fixedWidth={false}
                              style={{ width: "24px", height: "24px" }}
                              size={3}
                              mr={1}
                              name="copy"
                            />
                          </StyledAddButton>
                        )}
                      {data.length > 0 && (
                        <MinimalSelect
                          menuPortalTarget={document.body}
                          styles={{
                            container: (base) => ({
                              ...base,
                              width: "max-content",
                              minWidth: "100%",
                              marginRight: "8px",
                            }),
                            menu: (base) => ({
                              ...base,
                              width: "max-content",
                              minWidth: "100%",
                            }),
                            menuPortal: (baseStyles, state) => ({
                              ...baseStyles,
                              zIndex: 99999,
                            }),
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              color: "white !important",
                              cursor: "default !important",
                            }),
                            input: (baseStyles, state) => ({
                              ...baseStyles,
                              color: "white !important",
                            }),
                          }}
                          defaultValue={selectedData}
                          options={data}
                          onChange={(data) => {
                            reMapData?.(data);
                          }}
                        ></MinimalSelect>
                      )}
                      <StyledText style={{ marginRight: "8px" }}>
                        Credits: {selectedData?.credits || "0"}
                      </StyledText>
                      {selectedData?.average_score != null && (
                        <StyledText>
                          | Average Fit Score:{" "}
                          {selectedData?.average_score === ""
                            ? "0"
                            : selectedData?.average_score}
                        </StyledText>
                      )}
                    </TableCell>
                    {dateRange.map((row: any, index) => (
                      <TableCell
                        sx={{
                          background: backgroundColor || "rgba(0, 0, 0)",
                          color: "white",
                          borderBottom: "1px solid #3C406C",
                          position: "sticky",
                          left: "0",
                          top: "0",
                          zIndex: "700",
                          minWidth: "60px",
                          width: "60px",
                          maxWidth: "60px",
                          whiteSpace: "nowrap",
                        }}
                        key={index}
                        align="center"
                      />
                    ))}
                    <TableCell
                      sx={{
                        background: backgroundColor || "rgba(0, 0, 0)",
                        color: "white",
                        borderBottom: "1px solid #3C406C",
                        position: "sticky",
                        right: 0,
                        top: "0",
                        zIndex: "800",
                        paddingRight: "12px",
                      }}
                      align="right"
                    >
                      {isPersonalizedGenerated && <StyledAddButton
                        style={{ width: '200px', margin:0 }}
                        onClick={() => setIsPersonalizedGenerated && setIsPersonalizedGenerated(false)}
                      >
                        <MuiIcon style={{ marginRight: 10 }}>settings</MuiIcon> Change Settings
                      </StyledAddButton>}
                    </TableCell>
                  </>
                </TableRow>
                {/* <TableRow>
                  <>
                    <TableCell
                      sx={{
                        background: backgroundColor || 'rgba(0, 0, 0)',
                        color: 'white',
                        borderBottom: 'unset',
                        borderRight: '1px solid #3C406C',
                        minWidth: '60px',
                        maxWidth: '60px',
                        position: 'sticky',
                        left: 0,
                        zIndex: 800,
                      }}
                      key="date"
                      align="center"
                      style={{ top: 71 }}>
                      Date
                    </TableCell>
                    <TableCell
                      sx={{
                        background: backgroundColor || 'rgba(0, 0, 0)',
                        color: 'white',
                        borderBottom: 'unset',
                        borderRight: '1px solid #3C406C',
                        minWidth: requiredWidth || 'auto',
                      }}
                      colSpan={4}
                      key="required"
                      align="center"
                      style={{ top: 71 }}>
                      Required
                    </TableCell>
                    <TableCell
                      sx={{
                        background: backgroundColor || 'rgba(0, 0, 0)',
                        color: 'white',
                        borderBottom: 'unset',
                        borderRight: '1px solid #3C406C',
                        minWidth:
                          calculateOptWidth(requiredWidth, optionalWidth) ||
                          'auto',
                      }}
                      colSpan={4}
                      key="optional"
                      align="center"
                      style={{ top: 71 }}>
                      Optional
                    </TableCell>
                  </>
                </TableRow> */}
                <TableRow>
                  <>
                    <TableCell
                      sx={{
                        background: backgroundColor || "rgba(0, 0, 0)",
                        color: "white",
                        borderBottom: "unset",
                        borderRight: cellBackground
                          ? `1px solid ${cellBackground}`
                          : "1px solid #3C406C",
                        minWidth: "60px",
                        width: "60px",
                        maxWidth: "60px",
                        position: "sticky",
                        left: 0,
                        top: "71px",
                        zIndex: 900,
                      }}
                      key="date"
                      align="center"
                    ></TableCell>
                    {dateRange.map((row: any, index) => (
                      <>
                        {
                          // row.add_drop_date ? (
                          //   <TableCell
                          //     sx={{
                          //       background: backgroundColor || "rgba(0, 0, 0)",
                          //       color: "#00ff10",
                          //       borderBottom: "unset",
                          //       borderRight: cellBackground
                          //         ? `1px solid ${cellBackground}`
                          //         : "1px solid #3C406C",
                          //       minWidth: "120px",
                          //       width: "120px",
                          //       maxWidth: "120px",
                          //       position: "sticky",
                          //       fontWeight: "bold",
                          //       left: 0,
                          //       zIndex: 800,
                          //       top: "71px",
                          //     }}
                          //     key={"special for add"}
                          //     align="center"
                          //   >
                          //     <LightTooltip
                          //       title={
                          //         <React.Fragment>
                          //           <StyledText>Add drop date : {row.exact_date}</StyledText>
                          //         </React.Fragment>
                          //       }
                          //       arrow
                          //     >
                          //       <IconButton
                          //         style={{ padding: 0, verticalAlign: "top" }}
                          //       >
                          //         <InfoIcon style={{ color: "#00ff10" }} />
                          //       </IconButton>
                          //     </LightTooltip>
                          //     {row.date}
                          //   </TableCell>
                          // )

                          //-------------
                          // row.final_recording_date ? (
                          //   <TableCell
                          //     sx={{
                          //       background: backgroundColor || "rgba(0, 0, 0)",
                          //       color: "#00ff10",
                          //       borderBottom: "unset",
                          //       borderRight: cellBackground
                          //         ? `1px solid ${cellBackground}`
                          //         : "1px solid #3C406C",
                          //       minWidth: "120px",
                          //       width: "120px",
                          //       maxWidth: "120px",
                          //       position: "sticky",
                          //       fontWeight: "bold",
                          //       left: 0,
                          //       zIndex: 800,
                          //       top: "71px",
                          //     }}
                          //     key={"special for add"}
                          //     align="center"
                          //   >
                          //     <LightTooltip
                          //       title={
                          //         <React.Fragment>
                          //           <StyledText>
                          //             Final Assessment date : {row.assess_date}
                          //           </StyledText>
                          //           <br />
                          //           <StyledText>
                          //             Final Recording date : {row.exact_date}
                          //           </StyledText>
                          //         </React.Fragment>
                          //       }
                          //       arrow
                          //     >
                          //       <IconButton
                          //         style={{ padding: 0, verticalAlign: "top" }}
                          //       >
                          //         <InfoIcon style={{ color: "#00ff10" }} />
                          //       </IconButton>
                          //     </LightTooltip>
                          //     {row.date}
                          //   </TableCell>
                          // ) : 
                          // row.final_assess_date ? (
                          //   <TableCell
                          //     sx={{
                          //       background: backgroundColor || "rgba(0, 0, 0)",
                          //       color: "yellow",
                          //       borderBottom: "unset",
                          //       borderRight: cellBackground
                          //         ? `1px solid ${cellBackground}`
                          //         : "1px solid #3C406C",
                          //       minWidth: "120px",
                          //       width: "120px",
                          //       maxWidth: "120px",
                          //       position: "sticky",
                          //       fontWeight: "bold",
                          //       left: 0,
                          //       zIndex: 800,
                          //       top: "71px",
                          //     }}
                          //     key={"special for final"}
                          //     align="center"
                          //   >
                          //     <LightTooltip
                          //       title={
                          //         <React.Fragment>
                          //           <StyledText>
                          //             Final Assessment Date : {row.exact_date}
                          //           </StyledText>
                          //         </React.Fragment>
                          //       }
                          //       arrow
                          //     >
                          //       <IconButton
                          //         style={{ padding: 0, verticalAlign: "top" }}
                          //       >
                          //         <InfoIcon style={{ color: "yellow" }} />
                          //       </IconButton>
                          //     </LightTooltip>
                          //     {row.date}
                          //   </TableCell>
                          // ) :  // ----------------------
                          (
                            <TableCell
                              sx={{
                                background: backgroundColor || "rgba(0, 0, 0)",
                                color: "white",
                                borderBottom: "unset",
                                borderRight: cellBackground
                                  ? `1px solid ${cellBackground}`
                                  : "1px solid #3C406C",
                                minWidth: "120px",
                                width: "120px",
                                maxWidth: "120px",
                                position: "sticky",
                                left: 0,
                                zIndex: 800,
                                top: "71px",
                                height: 40
                              }}
                              key={index}
                              align="center"
                            >
                              {row.date}
                            </TableCell>
                          )
                        }
                      </>
                    ))}
                  </>
                </TableRow>
              </>
            )}
          </TableHead>
          <TableBody sx={{ position: "sticky" }}>
            <TableRow
              style={{
                height:
                  requiredWidth &&
                    requiredWidth !== "" &&
                    requiredWidth !== "30vh"
                    ? requiredWidth
                    : `calc(61.5vh - ${requiredWidth && requiredWidth !== "100px"
                      ? requiredWidth
                      : "175px"
                    })`,
                // height: requiredWidth,
                background: cellBackground,
              }}
              hover
              role="checkbox"
              tabIndex={-1}
              key={0}
            >
              <TableCell
                // colSpan={4}
                align="center"
                sx={{
                  color: "white",
                  border: backgroundColor
                    ? `1px solid ${backgroundColor}`
                    : "1px solid #3C406C",
                  borderBottom: "unset",
                  borderLeft: "unset",
                  width: "60px",
                  position: "sticky",
                  left: 0,
                  zIndex: 800,
                  background: "#303c68",
                }}
              >
                Required
              </TableCell>
              {dateRange.map((row: any, index) => (
                <TableCell
                  sx={{
                    background: "#303c68", //Required main background color
                    color: "white",
                    borderBottom: "unset",
                    borderRight: cellBackground
                      ? `1px solid ${cellBackground}`
                      : "1px solid #303c68",
                    minWidth: "60px",
                    width: "60px",
                    maxWidth: "60px",
                    position: "sticky",
                    left: 0,
                  }}
                  key={index}
                  align="center"
                />
              ))}
            </TableRow>
            <TableRow
              style={{
                verticalAlign: "top",
                height: "150vh",
                // isLarge              ?
                // '200vh',
                // : `calc(61.5vh - ${
                //     requiredWidth && requiredWidth !== "100px"
                //       ? requiredWidth
                //       : "175px"
                //   })`,
                background: cellBackground,
              }}
              hover
              role="checkbox"
              tabIndex={-1}
              key={12}
            >
              <TableCell
                // colSpan={4}
                align="center"
                sx={{
                  color: "white",
                  border: backgroundColor
                    ? `1px solid ${backgroundColor}`
                    : "1px solid #3C406C",
                  borderBottom: "unset",
                  borderLeft: "unset",
                  width: "60px",
                  position: "sticky",
                  left: 0,
                  zIndex: 800,
                  background: "#000920",
                }}
              >
                Optional
              </TableCell>
              {dateRange.map((row: any, index) => (
                <TableCell
                  sx={{
                    // height:"auto",
                    background: "#000920", // optional background
                    color: "white",
                    borderBottom: "unset",
                    borderRight: cellBackground
                      ? `1px solid ${cellBackground}`
                      : "1px solid #0c0d22",
                    minWidth: "60px",
                    width: "60px",
                    maxWidth: "60px",
                    position: "sticky",
                    left: 0,
                  }}
                  key={index}
                  align="center"
                />
              ))}
            </TableRow>
            {children}
          </TableBody>
        </Table>
      </CustomTableContainer>
    </CustomPaper>
  );
}
